import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Copy, ThumbsUp, ThumbsDown, RotateCcw, ExternalLink, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import chatService from '../../services/chatService';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Enhanced Chat Message with referenced items support
 * Shows AI messages with referenced items grid below
 */
export default function EnhancedChatMessage({ message, onItemClick, conversationId, onPinUpdate, onFollowUpClick }) {
  const { user } = useAuth();
  const [showActions, setShowActions] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isPinning, setIsPinning] = useState(false);

  // Check if message is saved as a moment on mount
  useEffect(() => {
    const checkMoment = async () => {
      if (user && message.id && message.type === 'ai') {
        // Convert message.id to string (handles both number and string formats)
        const messageIdStr = String(message.id);
        const result = await chatService.isMessagePinned(user.id, messageIdStr);
        // isMessagePinned now returns { data: id | null, error: null } for valid UUIDs
        // or { data: null, error: null } for invalid UUIDs (skipped query)
        setIsPinned(result.data !== null);
      }
    };
    checkMoment();
  }, [user, message.id, message.type]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handlePin = async () => {
    if (!user || !conversationId || !message.id || message.type !== 'ai' || isPinning) {
      console.warn('⚠️ [handlePin] Cannot pin: missing requirements', {
        hasUser: !!user,
        hasConversationId: !!conversationId,
        hasMessageId: !!message.id,
        messageType: message.type,
        isPinning
      });
      return;
    }

    try {
    if (isPinned) {
      // Remove moment - we need to find the moment ID first
      setIsPinning(true);
      const momentsResult = await chatService.getUserPinnedMoments(user.id);
      if (momentsResult.data) {
        // Test G: Cross-component reference - ensure string comparison for UUIDs
        const moment = momentsResult.data.find(m => {
          const match = String(m.message_id) === String(message.id);
          if (match) {
            console.log('✅ [TEST G] Found pinned moment match:', {
              momentMessageId: m.message_id,
              messageId: message.id,
              bothAreStrings: typeof m.message_id === 'string' && typeof message.id === 'string'
            });
          }
          return match;
        });
        if (moment) {
          const result = await chatService.unpinMoment(user.id, moment.id);
          if (result.data) {
            setIsPinned(false);
            if (onPinUpdate) onPinUpdate();
            console.log('✅ Moment removed successfully');
          } else {
            console.error('❌ Failed to remove moment:', result.error);
          }
        } else {
          console.warn('⚠️ Moment not found for message:', message.id);
        }
      } else {
        console.error('❌ Failed to load moments:', momentsResult.error);
      }
      setIsPinning(false);
    } else {
      // Save as moment
      setIsPinning(true);
      
      // Test E: Validate message ID before pinning
      const isValidUUID = typeof message.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(message.id);
      if (!isValidUUID) {
        console.error('❌ [TEST E FAILED] EnhancedChatMessage: Invalid message ID format:', {
          messageId: message.id,
          messageIdType: typeof message.id,
          isNumber: typeof message.id === 'number'
        });
      } else {
        console.log('✅ [TEST E] EnhancedChatMessage: Valid UUID before pinning:', message.id);
      }
      
      console.log('💫 Saving moment:', { 
        messageId: message.id, 
        messageIdType: typeof message.id,
        isValidUUID,
        conversationId 
      });
      const result = await chatService.pinMoment(
        user.id,
        conversationId,
        String(message.id), // Ensure it's a string
        message.content
      );
      if (result.data) {
        setIsPinned(true);
        if (onPinUpdate) onPinUpdate();
        console.log('✅ Moment saved successfully');
      } else {
        console.error('❌ Failed to save moment:', result.error);
        const errorMsg = result.error?.message || 'Failed to save moment';
        if (result.error?.code === 'TABLE_NOT_FOUND') {
          alert('Moments feature is not available. The database table needs to be created. Please contact support.');
        } else {
          alert(`Failed to save moment: ${errorMsg}`);
        }
      }
      setIsPinning(false);
    }
    } catch (error) {
      console.error('❌ [handlePin] Error:', error);
      setIsPinning(false);
      alert('An error occurred while saving/removing moment. Please try again.');
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} group`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className={`max-w-[75%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
        {/* Avatar and message container */}
        <div
          className={`flex items-start gap-3 ${
            message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
          }`}
        >
          {/* Avatar */}
          {message.type === 'ai' && (
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#7A47F5] to-[#9D6FF5] shadow-sm p-1.5">
              <svg 
                viewBox="500 300 1100 1400" 
                className="w-full h-full"
                preserveAspectRatio="xMidYMid meet"
              >
                <path 
                  fill="white" 
                  d="M 1164.22 315.416 C 1226.57 311.925 1301.55 320.971 1352.42 359.073 C 1389.34 386.728 1415.89 420.703 1421.8 466.98 C 1429.24 525.221 1418.64 581.381 1382.07 628.153 C 1334.13 689.473 1262.07 738.867 1183.74 748.707 C 1145.35 754.215 1101.89 750.712 1069.86 726.381 C 1046.32 708.505 1026.99 663.003 1048.02 636.427 C 1052.28 631.046 1063.15 624.143 1070.51 624.061 C 1095.77 623.782 1088.83 661.424 1107.65 672.179 C 1124.04 683.691 1146.6 686.047 1165.23 683.95 C 1209.4 678.977 1249.12 651.074 1276.51 617.326 C 1306 581.987 1317.83 539.557 1312.81 493.817 C 1303.37 407.762 1206.47 377.994 1132.28 388.792 C 1035.81 402.834 941.899 455.397 880.433 530.823 C 830.256 592.395 813.55 679.242 865.397 744.693 C 871.503 752.547 878.103 760.229 885.227 767.201 C 955.139 835.617 1047.3 878.027 1131.31 926.345 C 1188.27 959.099 1244.86 989.759 1296.98 1030.25 C 1356.72 1076.65 1403.27 1137.51 1411.7 1214.95 C 1421.33 1300.6 1393.24 1378.11 1339.97 1444.28 C 1248.37 1558.07 1100.97 1622.81 958.279 1638.76 C 846.435 1648.92 726.537 1629.99 636.56 1559.29 C 534.114 1478.79 526.595 1333.69 608.073 1235.77 C 654.169 1180.37 718.444 1134.95 791.291 1125.04 C 811.442 1122.3 849.065 1122.78 851.429 1150.12 C 853.464 1178.04 815.603 1191.49 796.211 1205.52 C 782.773 1215.12 770.559 1226.33 759.838 1238.89 C 695.15 1313.3 678.282 1428.11 759.769 1496.07 C 814.706 1541.88 889.784 1555.81 959.625 1551.15 C 1047.74 1545.27 1135.06 1502.38 1193.72 1436.49 C 1235.22 1389.37 1265.61 1315.71 1260.02 1252.2 C 1250.14 1139.97 1152.01 1079.22 1063.53 1030.09 L 970.158 978.703 C 903.638 941.507 850.734 907.97 790.784 860.011 C 663.61 761.404 682.623 611.639 782.669 501.454 C 880.816 393.361 1018.27 325.179 1164.22 315.416 z"
                />
              </svg>
            </div>
          )}
          {message.type === 'user' && (
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-blue-500 to-cyan-500 shadow-sm overflow-hidden">
              {user?.user_metadata?.name || user?.email ? (
                <span className="text-white text-xs font-semibold">
                  {(user?.user_metadata?.name || user?.email?.split('@')[0] || 'U').charAt(0).toUpperCase()}
                </span>
              ) : (
                <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-400 to-cyan-400"></div>
              )}
            </div>
          )}

          {/* Message content */}
          <div className="flex-1">
            <div className="flex flex-col gap-1">
              <div
                data-message-id={message.id}
                className={`inline-block px-4 py-3 rounded-xl transition-all ${
                  message.type === 'user'
                    ? 'bg-neutral-800 text-white'
                    : message.isError
                    ? 'bg-red-50 text-red-800 border border-red-200'
                    : 'bg-neutral-50 text-neutral-800 border border-neutral-200/50'
                }`}
              >
              {message.type === 'ai' ? (
                <div className="prose prose-sm max-w-none text-[15px] leading-relaxed">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Prevent huge headers
                      h1: ({node, ...props}) => <h1 className="text-xl font-semibold mb-3 mt-4 first:mt-0 text-neutral-900" {...props} />,
                      h2: ({node, ...props}) => <h2 className="text-lg font-semibold mb-2.5 mt-3.5 first:mt-0 text-neutral-900" {...props} />,
                      h3: ({node, ...props}) => <h3 className="text-base font-semibold mb-2 mt-3 first:mt-0 text-neutral-900" {...props} />,
                      p: ({node, ...props}) => <p className="mb-3 last:mb-0 text-neutral-800 leading-relaxed" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc list-outside mb-3 ml-4 space-y-2" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal list-outside mb-3 ml-4 space-y-2" {...props} />,
                      li: ({node, ...props}) => <li className="ml-2 text-neutral-800 leading-relaxed pl-1" {...props} />,
                      code: ({node, inline, ...props}) => 
                        inline ? (
                          <code className="bg-neutral-200/70 px-1.5 py-0.5 rounded text-sm font-mono text-purple-700" {...props} />
                        ) : (
                          <code className="block bg-neutral-200/70 p-3 rounded-md text-sm font-mono overflow-x-auto mb-3 text-neutral-800" {...props} />
                        ),
                      pre: ({node, ...props}) => <pre className="bg-neutral-200/70 p-3 rounded-md text-sm font-mono overflow-x-auto mb-3" {...props} />,
                      blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-purple-300 pl-4 italic my-3 text-neutral-700" {...props} />,
                      a: ({node, ...props}) => <a className="text-purple-600 hover:text-purple-700 underline font-medium" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-semibold text-neutral-900" {...props} />,
                      em: ({node, ...props}) => <em className="italic text-neutral-800" {...props} />,
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="whitespace-pre-wrap text-[15px] leading-relaxed">
                  {message.content}
                </p>
              )}
              </div>

              {/* Attachment Preview - Figma-style grid that adapts to number of images */}
              {message.type === 'user' && message.attachments && message.attachments.length > 0 && (
                <div className="mt-2">
                  <div className="grid grid-cols-3 gap-1.5 w-[270px]">
                    {message.attachments.slice(0, 9).map((attachment, index) => {
                      const hasMore = message.attachments.length > 9 && index === 8;
                      const remainingCount = message.attachments.length - 9;
                      
                      return (
                        <div
                          key={attachment?.id || index}
                          className="relative w-[90px] h-[90px] rounded-lg overflow-hidden bg-neutral-100"
                        >
                          {attachment.thumbnail || attachment.file_url ? (
                            <>
                              <img
                                src={attachment.thumbnail || attachment.file_url}
                                alt={attachment.label || attachment.file_name || 'Attachment'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                              {/* Fallback gradient */}
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200 hidden">
                                <span className="text-purple-600 text-sm font-bold">
                                  {(attachment.label || attachment.file_name || '?').charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200">
                              <span className="text-purple-600 text-sm font-bold">
                                {(attachment.label || attachment.file_name || '?').charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          {/* Fade mask overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
                          {/* +N badge - absolutely positioned bottom right */}
                          {hasMore && (
                            <div className="absolute bottom-1 right-1 bg-black/70 backdrop-blur-sm rounded-md px-1.5 py-0.5 flex items-center justify-center">
                              <span className="text-white text-xs font-semibold">
                                +{remainingCount}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Swirl Summary Block */}
            {message.type === 'ai' && message.summary && message.summary.trim() && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="mt-3 pt-3 border-t border-neutral-200/50"
              >
                <div className="text-xs font-semibold text-purple-600 mb-1.5">
                  Swirl Summary
                </div>
                <p className="text-xs text-neutral-600 leading-relaxed">
                  {message.summary}
                </p>
              </motion.div>
            )}

            {/* Citations Section */}
            {message.type === 'ai' && message.referencedItems && message.referencedItems.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-2"
              >
                <div className="text-xs font-medium text-neutral-500 mb-2">
                  Sources:
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {message.referencedItems.slice(0, 9).map((item, index) => {
                    const label = item.label || item.title || item.sourceId || 'Untitled';
                    const isWeb = item.type === 'web';
                    const hasUrl = item.url && isWeb;
                    
                    return (
                      <motion.div
                        key={item.sourceId || item.id || index}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="relative group"
                      >
                        {hasUrl ? (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/60 backdrop-blur-sm border border-neutral-200/50 hover:border-purple-300 hover:bg-purple-50/30 transition-all text-left"
                          >
                            <span className="text-xs text-neutral-700 truncate max-w-[120px]">
                              {label}
                            </span>
                            <ExternalLink
                              size={10}
                              className="text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                            />
                          </a>
                        ) : (
                          <button
                            onClick={() => onItemClick && onItemClick(item)}
                            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/60 backdrop-blur-sm border border-neutral-200/50 hover:border-purple-300 hover:bg-purple-50/30 transition-all text-left"
                          >
                            {item.thumbnail ? (
                              <img
                                src={item.thumbnail}
                                alt={label}
                                className="w-5 h-5 rounded object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-5 h-5 rounded flex items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200 flex-shrink-0">
                                <span className="text-purple-600 text-xs font-bold">
                                  {label.charAt(0).toUpperCase() || '?'}
                                </span>
                              </div>
                            )}
                            <span className="text-xs text-neutral-700 truncate max-w-[120px]">
                              {label}
                            </span>
                          </button>
                        )}
                      </motion.div>
                    );
                  })}
                  {message.referencedItems.length > 9 && (
                    <div className="px-2 py-1 rounded-md bg-neutral-100/60 border border-neutral-200/50 text-xs text-neutral-500">
                      +{message.referencedItems.length - 9} more
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Follow-up Chips */}
            {message.type === 'ai' && message.followUps && message.followUps.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="mt-3 pt-3 border-t border-neutral-200/50"
              >
                <div className="flex flex-wrap gap-2">
                  {message.followUps.map((followUp, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        if (onFollowUpClick) {
                          onFollowUpClick(followUp);
                        }
                      }}
                      className="px-3 py-1.5 rounded-full bg-purple-50 hover:bg-purple-100 border border-purple-200/50 hover:border-purple-300 text-xs font-medium text-purple-700 transition-all"
                    >
                      {followUp}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Timestamp */}
            <div
              className={`text-xs text-neutral-400 mt-1.5 ${
                message.type === 'user' ? 'text-right' : 'text-left'
              }`}
            >
              {formatTimestamp(message.timestamp)}
            </div>
          </div>
        </div>

        {/* Message actions */}
        <div
          className={`flex items-center gap-1 mt-2 relative z-50 ${
            message.type === 'user' ? 'justify-end' : 'justify-start'
          } ${!showActions ? 'invisible' : 'visible'}`}
        >
          {message.type === 'user' ? (
            <button
              onClick={handleCopy}
              className="p-2 rounded-lg bg-white/80 backdrop-blur-lg border border-white/50 text-gray-600 hover:bg-white/90 transition-all duration-200"
              title={copied ? 'Copied!' : 'Copy message'}
            >
              <Copy size={14} />
            </button>
          ) : (
            <>
              <button
                onClick={handleCopy}
                className="p-2 rounded-lg bg-white/80 backdrop-blur-lg border border-white/50 text-gray-600 hover:bg-white/90 transition-all duration-200"
                title={copied ? 'Copied!' : 'Copy message'}
              >
                <Copy size={14} />
              </button>
              <button
                onClick={() => console.log('Thumbs down')}
                className="p-2 rounded-lg bg-white/80 backdrop-blur-lg border border-white/50 text-gray-600 hover:bg-red-100 hover:text-red-600 transition-all duration-200"
                title="Poor response"
              >
                <ThumbsDown size={14} />
              </button>
              <button
                onClick={() => console.log('Thumbs up')}
                className="p-2 rounded-lg bg-white/80 backdrop-blur-lg border border-white/50 text-gray-600 hover:bg-green-100 hover:text-green-600 transition-all duration-200"
                title="Good response"
              >
                <ThumbsUp size={14} />
              </button>
              <button
                onClick={handlePin}
                disabled={isPinning}
                className={`p-2 rounded-lg bg-white/80 backdrop-blur-lg border border-white/50 transition-all duration-200 ${
                  isPinned
                    ? 'text-purple-600 bg-purple-50/80 border-purple-200 hover:bg-purple-100'
                    : 'text-gray-600 hover:bg-white/90'
                } ${isPinning ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={isPinned ? 'Remove moment' : 'Save Moment'}
              >
                <Sparkles size={14} className={isPinned ? 'fill-current' : ''} />
              </button>
              <button
                onClick={() => console.log('Regenerate')}
                className="p-2 rounded-lg bg-white/80 backdrop-blur-lg border border-white/50 text-gray-600 hover:bg-white/90 transition-all duration-200"
                title="Regenerate response"
              >
                <RotateCcw size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

